import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, Directive, Input, HostListener, ContentChild, Host, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { getRouteData, distinctUntilChangedDeep, hasNoValue, retrieveErrorMessage, DomainId, filterNullUndefined } from '@domgen/dgx-fe-common';
import { __decorate, __metadata } from 'tslib';
import * as i2 from '@angular/forms';
import { UntypedFormArray, UntypedFormGroup, NgControl } from '@angular/forms';
import { CountryDateFormat } from '@domgen/dgx-fe-i18n';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { format } from 'date-fns';
import { startWith, filter, skip, BehaviorSubject, combineLatest, pipe } from 'rxjs';
import { filter as filter$1, debounceTime, tap } from 'rxjs/operators';

var GAEvent;
(function (GAEvent) {
    GAEvent["GenericError"] = "errorGAEvent";
    GAEvent["GenericEvent"] = "genericGAEvent";
    GAEvent["PageView"] = "spa-pageview";
})(GAEvent || (GAEvent = {}));

const GA_CONFIG = new InjectionToken('GA Config');

const GA_GLOBAL_DATA = new InjectionToken('GA Global Data');

const GOOGLE_TAG_MANAGER = new InjectionToken('GoogleTagManager', {
    factory: () => {
        return (window?.dataLayer || {
            push: () => console.warn('window.dataLayer is not defined.'),
        });
    },
});

function extractPageNameFromUrl() {
    const pagename = window.location.pathname.replace(/\//, '');
    return pagename;
}

class GAService {
    constructor(gaConfig, gtm, route, gaGlobalData) {
        this.gaConfig = gaConfig;
        this.gtm = gtm;
        this.route = route;
        this.gaGlobalData = gaGlobalData;
        this.dynamicData = {};
        if (this.gaGlobalData) {
            this.dynamicData = { ...this.gaGlobalData };
        }
    }
    tag(...tags) {
        tags.forEach((tag) => {
            const gaTag = {
                pagename: extractPageNameFromUrl(),
                ...this.globalData,
                ...this.routeData,
                ...tag,
            };
            this.gtm.push(gaTag);
            if (this.gaConfig.debug) {
                console.log('%c🕵️ GTM Event', 'font-weight:bold; font-size:18px; margin-bottom:8px;');
                console.table(gaTag);
            }
        });
    }
    pageView(tag) {
        const pageView = {
            ...this.globalData,
            ...this.routeData,
            brand: this.gaConfig.clientBrandCode,
            country: this.gaConfig.countryCode,
            domainId: this.gaConfig.domainId,
            event: GAEvent.PageView,
            ...tag,
        };
        this.gtm.push(pageView);
        if (this.gaConfig.debug) {
            console.log('%c🕵️ GTM PageView', 'font-weight:bold; font-size:18px; margin-bottom:8px;');
            console.table(pageView);
        }
    }
    addToGlobalData(data) {
        this.dynamicData = { ...this.dynamicData, ...data };
    }
    removeFromGlobalData(key) {
        delete this.dynamicData[key];
    }
    get routeData() {
        const routeData = getRouteData(this.route.snapshot);
        return routeData.gaData;
    }
    get globalData() {
        return { ...this.dynamicData };
    }
}
GAService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAService, deps: [{ token: GA_CONFIG }, { token: GOOGLE_TAG_MANAGER }, { token: i1.ActivatedRoute }, { token: GA_GLOBAL_DATA, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
GAService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [GA_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [GOOGLE_TAG_MANAGER]
                }] }, { type: i1.ActivatedRoute }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [GA_GLOBAL_DATA]
                }] }]; } });

class GAContainerDirective {
    constructor() {
        this.containerData = {};
    }
    set dgxGaContainer(containerData) {
        this.containerData = containerData;
    }
}
GAContainerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
GAContainerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: GAContainerDirective, selector: "[dgxGaContainer]", inputs: { dgxGaContainer: "dgxGaContainer" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxGaContainer]',
                }]
        }], propDecorators: { dgxGaContainer: [{
                type: Input
            }] } });

class GAClickDirective {
    constructor(gaService, gaContainerDirective) {
        this.gaService = gaService;
        this.gaContainerDirective = gaContainerDirective;
        this.additionalData = {};
    }
    set dgxGaClick(additionalData) {
        this.additionalData = additionalData;
    }
    onClick() {
        this.gaService.tag({
            event: GAEvent.GenericEvent,
            ...this.gaContainerDirective?.containerData,
            ...this.additionalData,
        });
    }
}
GAClickDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAClickDirective, deps: [{ token: GAService }, { token: GAContainerDirective, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
GAClickDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: GAClickDirective, selector: "[dgxGaClick]", inputs: { dgxGaClick: "dgxGaClick" }, host: { listeners: { "click": "onClick($event.target)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxGaClick]',
                }]
        }], ctorParameters: function () { return [{ type: GAService }, { type: GAContainerDirective, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { dgxGaClick: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event.target']]
            }] } });

class GAFormControlComponent {
}

var GAFormFieldStatus;
(function (GAFormFieldStatus) {
    GAFormFieldStatus["Complete"] = "COMPLETE";
    GAFormFieldStatus["Invalid"] = "INVALID";
})(GAFormFieldStatus || (GAFormFieldStatus = {}));

function gaFormControlValidationErrors(control) {
    if (control instanceof UntypedFormArray) {
        return control.controls.map((child) => gaFormControlValidationErrors(child));
    }
    else if (control instanceof UntypedFormGroup) {
        return Object.entries(control.controls).reduce((validationErrors, [childKey, childControl]) => ({
            ...validationErrors,
            [childKey]: gaFormControlValidationErrors(childControl),
        }), {});
    }
    return control.errors;
}
function gaFormControlValidationErrorsToString(errors) {
    if (!errors) {
        return '';
    }
    else if (Array.isArray(errors)) {
        return errors
            .map((error) => gaFormControlValidationErrorsToString(error))
            .join(' | ');
    }
    return Object.entries(errors)
        .reduce((errorStrings, [controlKey, controlErrors]) => {
        if (!controlErrors) {
            return errorStrings;
        }
        else if (typeof controlErrors === 'string' || typeof controlErrors === 'boolean') {
            return [...errorStrings, `${controlKey}: ${controlErrors}`];
        }
        else if (Array.isArray(controlErrors)) {
            return [...errorStrings, `${controlKey}: ${controlErrors.join(', ')}`];
        }
        else if (typeof controlErrors === 'object') {
            const controlErrorMessages = Object.values(controlErrors).join(', ');
            return [...errorStrings, `${controlKey}: ${controlErrorMessages}`];
        }
        return errorStrings;
    }, [])
        .join(' | ');
}
function gaFormControlValue$(control) {
    return control.valueChanges.pipe(startWith(control.value), filter(() => control.enabled), distinctUntilChangedDeep());
}
function gaFormControlValueChanges$(control) {
    return gaFormControlValue$(control).pipe(skip(1));
}

let GAFormFieldDirective = class GAFormFieldDirective {
    constructor(gaService, ngControl, gaContainerDirective) {
        this.gaService = gaService;
        this.ngControl = ngControl;
        this.gaContainerDirective = gaContainerDirective;
        this.trackFormField = 'value';
        this.trackFormFieldInitialValue = false;
        this.trackFormFieldInvalid = false;
        this.additionalData = {};
        this.isTypingSubject = new BehaviorSubject(false);
        this.onKeydown = () => {
            this.isTypingSubject.next(true);
        };
    }
    set dgxGaFormField(additionalData) {
        this.additionalData = typeof additionalData === 'object' ? additionalData : {};
    }
    ngOnInit() {
        this.control = this.ngControl.control;
        this.controlName = this.ngControl.name;
        if (!this.control) {
            return console.error('dgxGaFormField must be used on an ngControl');
        }
        if (this.trackFormFieldInitialValue) {
            this.trackFormFieldEvent();
        }
        this.listenToIsTyping();
        this.listenToControlValueChanges();
    }
    listenToIsTyping() {
        this.isTypingSubject
            .pipe(filter$1((isTyping) => isTyping), debounceTime(1000), untilDestroyed(this))
            .subscribe(() => this.isTypingSubject.next(false));
    }
    listenToControlValueChanges() {
        const control = this.control;
        if (!control) {
            return;
        }
        combineLatest([gaFormControlValueChanges$(control), this.isTypingSubject])
            .pipe(filter$1(([, isTyping]) => !isTyping), untilDestroyed(this))
            .subscribe(([controlValue]) => {
            if ((!this.trackFormFieldInvalid && control.invalid) ||
                (control.valid && hasNoValue(controlValue))) {
                return;
            }
            this.trackFormFieldEvent();
        });
    }
    trackFormFieldEvent() {
        this.gaService.tag({
            event: GAEvent.GenericEvent,
            eventAction: this.controlName,
            eventLabel: this.label,
            ...this.gaContainerDirective?.containerData,
            ...this.additionalData,
        });
    }
    get label() {
        const controlValue = this.control?.value;
        const options = this.formControlComponent?.options ?? [];
        const selectedOption = options.find((option) => option.value == controlValue);
        switch (this.trackFormField) {
            case 'status':
                return this.ngControl.valid ? GAFormFieldStatus.Complete : GAFormFieldStatus.Invalid;
            case 'option':
                return selectedOption?.label ?? controlValue;
            case 'value':
            default:
                return controlValue instanceof Date
                    ? format(controlValue, CountryDateFormat.GreatBritain)
                    : controlValue;
        }
    }
};
GAFormFieldDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAFormFieldDirective, deps: [{ token: GAService }, { token: i2.NgControl }, { token: GAContainerDirective, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
GAFormFieldDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: GAFormFieldDirective, selector: "[dgxGaFormField]", inputs: { dgxGaFormField: "dgxGaFormField", trackFormField: "trackFormField", trackFormFieldInitialValue: "trackFormFieldInitialValue", trackFormFieldInvalid: "trackFormFieldInvalid" }, host: { listeners: { "keydown": "onKeydown()" } }, queries: [{ propertyName: "formControlComponent", first: true, predicate: GAFormControlComponent, descendants: true }], ngImport: i0 });
GAFormFieldDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [GAService,
        NgControl,
        GAContainerDirective])
], GAFormFieldDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAFormFieldDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxGaFormField]',
                }]
        }], ctorParameters: function () { return [{ type: GAService }, { type: i2.NgControl }, { type: GAContainerDirective, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { dgxGaFormField: [{
                type: Input
            }], trackFormField: [{
                type: Input
            }], trackFormFieldInitialValue: [{
                type: Input
            }], trackFormFieldInvalid: [{
                type: Input
            }], formControlComponent: [{
                type: ContentChild,
                args: [GAFormControlComponent]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown']
            }] } });

class GAFormSubmitDirective {
    constructor(gaService, gaContainerDirective) {
        this.gaService = gaService;
        this.gaContainerDirective = gaContainerDirective;
        this.additionalData = {};
        this.onSubmit = () => {
            if (this.formGroup.disabled) {
                return;
            }
            this.formGroup.valid ? this.trackFormValidSubmit() : this.trackFormInvalidSubmit();
        };
    }
    set dgxGaFormSubmit(additionalData) {
        this.additionalData = typeof additionalData === 'object' ? additionalData : {};
    }
    trackFormValidSubmit() {
        this.gaService.tag({
            event: GAEvent.GenericEvent,
            eventAction: 'submit form: true',
            eventLabel: 'success',
            ...this.gaContainerDirective?.containerData,
            ...this.additionalData,
        });
    }
    trackFormInvalidSubmit() {
        const errors = gaFormControlValidationErrors(this.formGroup);
        const eventLabel = gaFormControlValidationErrorsToString(errors);
        this.gaService.tag({
            event: GAEvent.GenericEvent,
            eventAction: 'submit form: false',
            eventLabel,
            ...this.gaContainerDirective?.containerData,
            ...this.additionalData,
        });
    }
}
GAFormSubmitDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAFormSubmitDirective, deps: [{ token: GAService }, { token: GAContainerDirective, host: true, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
GAFormSubmitDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: GAFormSubmitDirective, selector: "[dgxGaFormSubmit]", inputs: { dgxGaFormSubmit: "dgxGaFormSubmit", formGroup: "formGroup", name: "name" }, host: { listeners: { "submit": "onSubmit()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAFormSubmitDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxGaFormSubmit]',
                }]
        }], ctorParameters: function () { return [{ type: GAService }, { type: GAContainerDirective, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }]; }, propDecorators: { dgxGaFormSubmit: [{
                type: Input
            }], formGroup: [{
                type: Input
            }], name: [{
                type: Input
            }], onSubmit: [{
                type: HostListener,
                args: ['submit']
            }] } });

class DgxGAModule {
    constructor(injector) {
        DgxGAModule.injector = injector;
    }
    static forRoot() {
        return {
            ngModule: DgxGAModule,
            providers: [GAService],
        };
    }
}
DgxGAModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxGAModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule });
DgxGAModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DgxGAModule, declarations: [GAClickDirective,
        GAContainerDirective,
        GAFormFieldDirective,
        GAFormSubmitDirective], imports: [CommonModule], exports: [GAClickDirective, GAContainerDirective, GAFormFieldDirective, GAFormSubmitDirective] });
DgxGAModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxGAModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxGAModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        GAClickDirective,
                        GAContainerDirective,
                        GAFormFieldDirective,
                        GAFormSubmitDirective,
                    ],
                    exports: [GAClickDirective, GAContainerDirective, GAFormFieldDirective, GAFormSubmitDirective],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

function decorateNgOnInit(ngOnInit, gaPageData) {
    return function () {
        if (ngOnInit) {
            ngOnInit.call(this);
        }
        if (DgxGAModule.injector) {
            this.gaService = DgxGAModule.injector.get(GAService);
            const gaData = {
                ...this.gaService.routeData,
                ...gaPageData,
            };
            this.gaService.pageView({
                event: GAEvent.PageView,
                pagename: gaData.pagename || extractPageNameFromUrl(),
                ...gaData.additionalTags,
            });
        }
    };
}
function GAPage(gaPageData = {}) {
    return function (constructor) {
        constructor.prototype.ngOnInit = decorateNgOnInit(constructor.prototype.ngOnInit, gaPageData);
    };
}

class GAHttpErrorInterceptor {
    constructor(gaConfig, gaService) {
        this.gaConfig = gaConfig;
        this.gaService = gaService;
    }
    intercept(request, next) {
        return next.handle(request).pipe(tap({
            error: (error) => {
                const requestPath = this.mapUrlPath(request.url);
                const gaHttpErrorConstructor = this.gaConfig.httpErrorMapping[requestPath];
                gaHttpErrorConstructor
                    ? this.gaService.tag(gaHttpErrorConstructor(request, error))
                    : this.gaService.tag({
                        event: GAEvent.GenericError,
                        errorCode: error.status,
                        errorMessage: retrieveErrorMessage(error),
                        url: request.url,
                    });
            },
        }));
    }
    mapUrlPath(url) {
        const parser = document.createElement('a');
        parser.href = url;
        return parser.pathname;
    }
}
GAHttpErrorInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAHttpErrorInterceptor, deps: [{ token: GA_CONFIG }, { token: GAService }], target: i0.ɵɵFactoryTarget.Injectable });
GAHttpErrorInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAHttpErrorInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GAHttpErrorInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [GA_CONFIG]
                }] }, { type: GAService }]; } });

var GACountryCode;
(function (GACountryCode) {
    GACountryCode["France"] = "FR";
    GACountryCode["Germany"] = "DE";
    GACountryCode["GreatBritain"] = "GB";
    GACountryCode["Ireland"] = "IE";
    GACountryCode["Italy"] = "IT";
    GACountryCode["Spain"] = "ES";
    GACountryCode["UnitedStates"] = "USA";
})(GACountryCode || (GACountryCode = {}));

const mockGaHttpError = {
    category: 'quote-api',
    errorCode: 500,
    errorMessage: 'error',
    event: GAEvent.GenericError,
    url: 'url',
};

const mockGaHttpErrorConstructor = () => mockGaHttpError;

const mockGaHttpErrorMapping = {
    ['/waffles']: mockGaHttpErrorConstructor,
};

const mockGaConfig = {
    clientBrandCode: undefined,
    countryCode: GACountryCode.GreatBritain,
    debug: false,
    domainId: DomainId.DomesticAndGeneral,
    httpErrorMapping: mockGaHttpErrorMapping,
};

const mockGaTag = {
    eventLabel: 'eventLabel',
};

const mockGAPageData = {
    additionalTags: mockGaTag,
    category: 'category',
    event: GAEvent.PageView,
    pagename: 'pagename',
};

const mockGaRouteData = {
    pagename: 'pagename',
    eventCategory: 'eventCategory',
};

const mockGaPageView = {
    ...mockGaRouteData,
    ...mockGaTag,
    brand: undefined,
    country: GACountryCode.GreatBritain,
    domainId: mockGaConfig.domainId,
    event: GAEvent.GenericError,
};

var GAAnonymousValue;
(function (GAAnonymousValue) {
    GAAnonymousValue["Provided"] = "provided";
    GAAnonymousValue["NotProvided"] = "not-provided";
})(GAAnonymousValue || (GAAnonymousValue = {}));

var GAUserType;
(function (GAUserType) {
    GAUserType["Guest"] = "G";
    GAUserType["LoggedIn"] = "L";
})(GAUserType || (GAUserType = {}));

function pushToGa(gaTaggingService) {
    return pipe(filterNullUndefined(), tap((gaTags) => gaTaggingService.tag(gaTags)));
}

function GAAnonymiseObject(obj) {
    if (!obj) {
        return {};
    }
    return Object.entries(obj).reduce((anonymous, [key, value]) => ({
        ...anonymous,
        [key]: GAAnonymiseValue(value),
    }), {});
}
function GAAnonymiseValue(value) {
    if (Array.isArray(value)) {
        return value.length ? GAAnonymousValue.Provided : GAAnonymousValue.NotProvided;
    }
    else if (value === null || value === undefined) {
        return GAAnonymousValue.NotProvided;
    }
    else if (typeof value === 'object') {
        return GAAnonymiseObject(value);
    }
    else if (typeof value === 'string') {
        return value.trim().length > 0 ? GAAnonymousValue.Provided : GAAnonymousValue.NotProvided;
    }
    else if (typeof value === 'boolean') {
        return value ? GAAnonymousValue.Provided : GAAnonymousValue.NotProvided;
    }
    return GAAnonymousValue.Provided;
}

function GAFlatten(items, description, operation) {
    return items
        .reduce((flattenedItems, item, index) => {
        let flattenedItem = `${description} ${index + 1}: `;
        flattenedItem += typeof operation === 'function' ? operation(item) : item[operation];
        return [...flattenedItems, flattenedItem];
    }, [])
        .join('| ');
}

/**
 * Generated bundle index. Do not edit.
 */

export { DgxGAModule, GAAnonymiseObject, GAAnonymiseValue, GAAnonymousValue, GAClickDirective, GAContainerDirective, GACountryCode, GAEvent, GAFlatten, GAFormControlComponent, GAFormFieldDirective, GAFormFieldStatus, GAFormSubmitDirective, GAHttpErrorInterceptor, GAPage, GAService, GAUserType, GA_CONFIG, GA_GLOBAL_DATA, GOOGLE_TAG_MANAGER, extractPageNameFromUrl, gaFormControlValidationErrors, gaFormControlValidationErrorsToString, gaFormControlValue$, gaFormControlValueChanges$, mockGAPageData, mockGaConfig, mockGaHttpError, mockGaHttpErrorConstructor, mockGaHttpErrorMapping, mockGaPageView, mockGaRouteData, mockGaTag, pushToGa };
